import { Component, OnInit, Input, inject, ChangeDetectorRef } from '@angular/core';
import { ApplyPanelComponent, ButtonDirective, NumberInputComponent, SliderComponent } from '@imagise/shared';
import { EditMode } from '../../../domain/enums/editor.enum';
import { EditorRotateDraftOperation } from '../../../domain';
import { Size2D } from '../../../domain/interfaces/shared';
import { RotateViewModel } from '../../../domain/models/rotate-view-model';
import { EditOptionsPanelBase } from '../edit-options-panel.base';
import { PanelLayoutComponent } from '../panel-layout/panel-layout.component';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'lib-rotate-panel',
  host: {
    class: 'contents',
  },
  templateUrl: './rotate-panel.component.html',
  imports: [
    ApplyPanelComponent,
    ButtonDirective,
    MatIconModule,
    NumberInputComponent,
    PanelLayoutComponent,
    SliderComponent,
    TranslateModule,
  ],
})
export class RotatePanelComponent extends EditOptionsPanelBase<EditorRotateDraftOperation> implements OnInit {
  private readonly changeDetector = inject(ChangeDetectorRef);

  @Input() set imageSize(value: Size2D | null) {
    this.refresh();
  }

  private viewModel = new RotateViewModel();

  angleValue = 0;

  get minPreciseAngle(): number {
    return RotateViewModel.MIN_PRECISE_ANGLE;
  }

  get maxPreciseAngle(): number {
    return RotateViewModel.MAX_PRECISE_ANGLE;
  }

  get flipX(): boolean {
    return this.viewModel.flipX;
  }

  get flipY(): boolean {
    return this.viewModel.flipY;
  }

  get isCutAdjustment(): boolean {
    return this.viewModel.cut;
  }

  flipHor(flip: boolean) {
    this.viewModel.flipX = flip;
    this.emitRotateOptions();
  }

  flipVert(flip: boolean) {
    this.viewModel.flipY = flip;
    this.emitRotateOptions();
  }

  rotateCW() {
    this.viewModel.rotateClockwise();
    this.syncAngleValue();
    this.emitRotateOptions();
  }

  rotateCCW() {
    this.viewModel.rotateCounterClockwise();
    this.syncAngleValue();
    this.emitRotateOptions();
  }

  onAdjustmentTypeChanged(type: 'fit' | 'cut') {
    const cut = type === 'cut';

    if (this.viewModel.cut === cut) {
      return;
    }

    this.viewModel.cut = cut;
    this.emitRotateOptions();
  }

  onAngleChanged(value: number): void {
    this.setPreciseAngle(value);
  }

  ngOnInit(): void {
    this.syncAngleValue();
    this.emitRotateOptions();
  }

  private emitRotateOptions() {
    this.changed.emit({
      kind: 'rotate',
      mode: EditMode.Rotate,
      params: this.viewModel.getRotateParams(),
    });
  }

  protected refresh(): void {
    this.viewModel = new RotateViewModel();
    this.syncAngleValue();
    // Reset here as well: the imageSize setter calls refresh() directly,
    // bypassing the base reset.
    this.changed.reset();
    this.emitRotateOptions();
  }

  private syncAngleValue(): void {
    this.angleValue = this.viewModel.totalAngle;
    this.changeDetector.markForCheck();
  }

  private setPreciseAngle(value: number): void {
    this.viewModel.setTotalAngle(value);
    this.syncAngleValue();
    this.emitRotateOptions();
  }
}
